/**
 * Компонент не завершен
 * Экспериментальная копия reactApp/components/SharingWindow/SharingNew/Sharing/SharingNewSharing.tsx
 * Изменения относительно оригинала помечены комментарием - tempexp_17779
 **/

import { ReactComponent as LockIcon } from '@vkontakte/icons/src/svg/20/lock_outline_20.svg';
import classNames from 'clsx';
import React, { type ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_MOBILE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { SHARE_SPACE_CONSUMPTION_WARNING_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/shareSpaceConsumptionWarning';
import { SharingNewAddUser } from 'reactApp/components/SharingWindow/blocks/AddUser/SharingNewAddUser';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { type EAccessRights, EFrom } from 'reactApp/components/SharingWindow/Sharing.types';
import { renderDeleteUsersDialog } from 'reactApp/components/SharingWindow/ui/Sharing/SharingNewSharing.helpers';
import { contactsSlice } from 'reactApp/modules/contacts/contacts.module';
import {
    accessControlListLoadStart,
    accessControlListReset,
    deleteAllUsers,
    setAccessControlListError,
    setAccessRight,
} from 'reactApp/modules/folderAccessControlList/folderAccessControlList.actions';
import {
    getACL,
    getError,
    getOwner,
    isAccessControlListLoading,
} from 'reactApp/modules/folderAccessControlList/folderAccessControlList.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUser } from 'reactApp/modules/user/user.thunkActions';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { SpaceConsumptionWarning } from '../../ui/SpaceConsumptionWarning/SpaceConsumptionWarning';
import { UserList } from '../UserList/UserList';
import styles from './SharingNewSharingTab.css';
import type { SharingNewSharingTabProps } from './SharingNewSharingTab.types';

// eslint-disable-next-line max-lines-per-function
export const SharingNewSharingTab = React.memo(function SharingNewSharingTab(props: SharingNewSharingTabProps): ReactElement | null {
    const dispatch = useDispatch();

    const { isLoaded, hasError: isUserError } = useSelector(UserSelectors.getLifeCycleState);
    const acList = useSelector(getACL);
    const owner = useSelector(getOwner);
    const currentUserEmail = useSelector(UserSelectors.getEmail);
    const isLoading = useSelector(isAccessControlListLoading);
    const error = useSelector(getError);

    const storage = useSelector(getCurrentStorage);
    const { isSharedIncoming } = getStorage(storage);

    // Блок раскрыт если есть приглашенные пользователи и если пользователь пришел из блока "Настроить доступ"
    const isFromSharing = props.from === EFrom.SHARING;

    const [isCloudUsersOpened, setCloudUsersOpened] = useState<boolean>(isFromSharing);

    const isUserLoaded = isLoaded && !isUserError;
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const { item, onClose, isMounted: mounted, isPhone, fetchedDataOnMount, setFetchedDataOnMount } = props;

    // FIXME: CLOUDWEB-13574. Костыль на случай если у монтированной папки не флага "mounted"
    const isMounted = mounted || currentUserEmail !== owner?.email;

    const loadAllData = useCallback(() => {
        dispatch(contactsSlice.actions.listRequest);
        dispatch(accessControlListLoadStart({ id: item?.id || '', storage: item?.storage }));
    }, [dispatch, item?.id, item?.storage]);

    useEffect(() => {
        // tempexp_17779-start
        if (fetchedDataOnMount) {
            return;
        }
        // tempexp_17779-end

        if (!isUserLoaded) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            dispatch(loadUser).then(() => loadAllData());
        } else {
            loadAllData();
        }

        setFetchedDataOnMount?.(true);

        return function cleanup() {
            dispatch(accessControlListReset());
        };
    }, [dispatch, fetchedDataOnMount, isUserLoaded, loadAllData, setFetchedDataOnMount]);

    useEffect(() => {
        if (isFromSharing) {
            return;
        }

        const needOpen = Boolean(acList.length);

        // Если в списке есть юзеры, а он скрыт - надо открыть. Для первого открытия со списком.
        if (!isCloudUsersOpened && needOpen && !isLoading) {
            setCloudUsersOpened(true);
        }
    }, [acList.length, isCloudUsersOpened, isFromSharing, isLoading]);

    const onActionClick = useCallback(() => {
        if (!item) {
            return;
        }

        sendGa('delete-all');
        dispatch(deleteAllUsers({ item, variant: props.variant }));
        setCloudUsersOpened(false);
    }, [dispatch, item, props.variant]);

    const handleAddUser = useCallback(
        ({ currentEmail, accessRight }: { currentEmail: string; accessRight: EAccessRights }) => {
            const existingAccount = acList.find((ac) => ac.email === currentEmail);

            if (existingAccount) {
                const needChangeAccessRights = existingAccount.accessRights !== accessRight;
                const text = needChangeAccessRights
                    ? 'Участник уже был добавлен. Вы изменили его права доступа'
                    : 'Участник с такими правами доступа уже добавлен';
                snackbarController.showSnackbar({
                    type: SnackbarTypes.warning,
                    text,
                    id: 'ownerExistError',
                    closable: true,
                });

                if (!needChangeAccessRights) {
                    return;
                }
            }

            dispatch(
                setAccessRight({
                    accessRight,
                    folderId: item?.id || '',
                    email: currentEmail,
                })
            );
            dispatch(contactsSlice.actions.listRequest());

            const xrayUserType = isBizUser ? 'biz' : 'b2c';

            sendGa('add-user');
            sendDwh({
                eventCategory: ECategoryGa.sharedFolder,
                action: 'add-user',
                label: xrayUserType,
            });
            sendPaymentGa({
                eventCategory: item?.weblink ? ECategoryGa.public : ECategoryGa.sharedFolder,
                action: 'created-sharing',
                public_id: item?.weblink,
            });
        },
        [acList, dispatch, item?.id, item?.weblink, isBizUser]
    );

    const onCloseAccessClick = useCallback(() => {
        renderDeleteUsersDialog({ onActionClick, isBizVariant: props.variant === SHARING_VARIANT.foldersBiz });
    }, [onActionClick, props.variant]);

    const clearError = useCallback(() => {
        dispatch(setAccessControlListError({ error: '' }));
    }, [dispatch]);

    if (isMounted) {
        return (
            <UserList
                acList={acList}
                isLoading={isLoading}
                isMounted={isMounted}
                isPhone={isPhone}
                isSharedIncoming={isSharedIncoming}
                onClose={onClose}
                item={item}
                owner={owner}
                variant={props.variant}
            />
        );
    }

    return (
        <div
            className={classNames(styles.root, { [styles.root_phone]: isPhone })}
            data-qa-section="cloud-users"
            data-qa-id="cloud-users-sharing"
        >
            {SHARE_SPACE_CONSUMPTION_WARNING_ENABLED && !IS_MOBILE_BROWSER && (
                <SpaceConsumptionWarning isBizVariant={props.variant === SHARING_VARIANT.foldersBiz} />
            )}
            <SharingNewAddUser
                error={error}
                clearError={clearError}
                isLoading={isLoading}
                handleAddUser={handleAddUser}
                isMounted={isMounted}
                isPhone={isPhone}
                variant={SHARING_VARIANT.folders}
            />
            <UserList
                acList={acList}
                isLoading={isLoading}
                isMounted={isMounted}
                isPhone={isPhone}
                isSharedIncoming={isSharedIncoming}
                onClose={onClose}
                item={item}
                owner={owner}
                variant={props.variant}
            />
            <Button
                qaId="sharing-mount-disable"
                disabled={acList.length === 0}
                theme={'base'}
                middle
                onClick={onCloseAccessClick}
                className={styles.btnContainer}
                sizeMode={isPhone ? ButtonSizeMode.big : ButtonSizeMode.middle}
            >
                <div className={styles.closeAccessButton}>
                    <LockIcon width={20} height={20} />
                    <span>Закрыть доступ</span>
                </div>
            </Button>
        </div>
    );
});
